/**
 * Choose which QuestionnaireResponse the Appointment page should show for a
 * cohort Group Therapy session: the shared Group note (calendar / group ops)
 * or a patient's fan-out copy (opened from that client's chart).
 */
import type { Appointment, QuestionnaireResponse } from '@medplum/fhirtypes';
import {
  getAppointmentGroupRef,
  getAppointmentSharedNoteRef,
  isCohortGroupAppointment,
} from '@/lib/groupTherapy';
import type { CohortNoteKind } from '../components/CohortProgressNoteTitleExtras';

/**
 * Chart navigation hands the page an Appointment built from chart rows, which carries no
 * Group link — fall back to the shared Group-subject note to detect a cohort session.
 */
export function isCohortNoteContext<T extends { response: QuestionnaireResponse }>(
  appointment: Appointment,
  entries: T[],
): boolean {
  if (isCohortGroupAppointment(appointment)) return true;
  return entries.some((e) => e.response.subject?.reference?.startsWith('Group/') ?? false);
}

/**
 * An appointment can end up tagged with more than one Group-subject note, so match in
 * priority order rather than taking the first Group-subject entry: the Appointment's
 * group-shared-note link is the authoritative pointer when present.
 */
export function findSharedCohortNoteEntry<T extends { response: QuestionnaireResponse }>(
  entries: T[],
  appointment: Appointment,
): T | undefined {
  const sharedRef = getAppointmentSharedNoteRef(appointment)?.reference;
  const groupRef = getAppointmentGroupRef(appointment)?.reference;
  const byExtension = sharedRef
    ? entries.find((e) => e.response.id && `QuestionnaireResponse/${e.response.id}` === sharedRef)
    : undefined;
  if (byExtension) return byExtension;
  const byGroupSubject = groupRef
    ? entries.find((e) => e.response.subject?.reference === groupRef)
    : undefined;
  return byGroupSubject
    ?? entries.find((e) => e.response.subject?.reference?.startsWith('Group/') ?? false);
}

export function findPatientCohortNoteEntry<T extends { response: QuestionnaireResponse }>(
  entries: T[],
  patientId: string,
): T | undefined {
  const subject = `Patient/${patientId}`;
  return entries.find((e) => e.response.subject?.reference === subject);
}

export function selectCohortAppointmentNote<T extends { response: QuestionnaireResponse }>(
  entries: T[],
  appointment: Appointment,
  chartPatientId: string | undefined,
  pickFallback: (entries: T[]) => T | undefined,
): { entry: T | undefined; noteKind: CohortNoteKind } {
  if (chartPatientId) {
    return {
      entry: findPatientCohortNoteEntry(entries, chartPatientId),
      noteKind: 'individual',
    };
  }
  return {
    entry: findSharedCohortNoteEntry(entries, appointment) ?? pickFallback(entries),
    noteKind: 'shared',
  };
}
